import { useQuery } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import type { FeatureFlagName } from "../FeatureFlagName";

import { featureFlagQueryKeys } from "./featureFlagQueryKeys";

/**
 * Returns feature flag states
 */
export function useFeatureFlagStates() {
  const { featureFlagService } = useDomainServices();

  return useQuery<Record<string, Record<string, boolean>>, Error>({
    queryKey: featureFlagQueryKeys.states(),
    queryFn: ({ signal }) =>
      featureFlagService.checkAllFeatureFlags({ abortSignal: signal }),
    // Flags should be frozen for the session to make a predictable experience
    staleTime: Infinity,
  });
}

/**
 * Check whether a single feature flag is enabled for the current user, within
 * `orgId` — defaulting to the current org.
 */
export function useCheckFeatureFlag(
  flagName: FeatureFlagName,
  orgId?: string,
): boolean {
  const resolvedOrg = useCurrentOrgId(orgId);
  const { data } = useFeatureFlagStates();

  // Should practically never default to false because we prefetch at the auth layer
  return data?.[resolvedOrg]?.[flagName] ?? false;
}
